import { z } from "zod";
import { defineStepConfig } from "../../registry";

/**
 * Snooze Workflow Rerun Step
 *
 * Cancels the current workflow run and schedules the same workflow definition
 * to be re-dispatched against the same subject entity after `delayMinutes`.
 *
 * Shared tool/step: the same config backs both the deterministic workflow node
 * (branched to when upstream data isn't ready) and the AI agent tool
 * (invoked when the agent determines upstream sync hasn't caught up).
 *
 * Introduced as a temporary primitive alongside GEN-1861 (Famous status hack);
 * it remains generic — any workflow can branch to this step to defer itself.
 */
export const snoozeWorkflowRerunStep = defineStepConfig({
  type: "snooze_workflow_rerun",
  name: "Snooze Workflow Rerun",
  description:
    "Cancel the current workflow run and schedule the same workflow to rerun after a delay. Useful when upstream data isn't yet ready to process.",
  category: "Control Flow",
  inputSchema: z.object({
    reason: z
      .string()
      .describe(
        "Why the workflow is being snoozed (surfaced in logs/analytics)",
      ),
    unreadyReferences: z
      .array(
        z.object({
          type: z.string(),
          id: z.string(),
          currentStatus: z.string().nullable(),
        }),
      )
      .optional()
      .describe("Optional structured context about what wasn't ready"),
  }),
  outputSchema: z.object({
    scheduled: z.boolean(),
    scheduledFor: z.string().describe("ISO timestamp of scheduled rerun"),
  }),
  configSchema: z.object({
    delayMinutes: z
      .number()
      .int()
      .min(1)
      .max(10080)
      .describe("How long to wait before re-dispatching the workflow"),
  }),
  emits: [] as const,
});
